import React, { useState, useEffect } from 'react';
import { Heart, Plus, X } from 'lucide-react';
import profileExtendedService from '../../services/profileExtended.service';
import toast from 'react-hot-toast';
import Button from '@components/common/Button';

const PersonalityTraitsSection = () => {
  const [userTraits, setUserTraits] = useState([]);
  const [allTraits, setAllTraits] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTraitIds, setSelectedTraitIds] = useState([]);

  useEffect(() => {
    fetchUserTraits();
    fetchAllTraits();
  }, []);

  useEffect(() => {
    if (userTraits.length > 0) {
      setSelectedTraitIds(userTraits.map(ut => ut.personalityTraitId));
    } else {
      setSelectedTraitIds([]);
    }
  }, [userTraits]);

  const fetchUserTraits = async () => {
    try {
      const response = await profileExtendedService.getPersonalityTraits();
      
      // Check if response indicates failure (treat as no traits added yet)
      if (response.success === false || response.data?.success === false) {
        console.warn('No traits found or API error:', response.message || response.data?.message);
        setUserTraits([]);
        return;
      }
      
      // Handle both response.data.data and response.data structures
      const traitsData = response.data?.data || response.data || [];
      setUserTraits(Array.isArray(traitsData) ? traitsData : []);
    } catch (error) {
      console.error('Failed to fetch personality traits:', error);
      console.error('Error details:', error.response?.data);
      // Treat error as no traits (user hasn't added any yet)
      setUserTraits([]);
    }
  };

  const fetchAllTraits = async () => {
    try {
      const response = await profileExtendedService.getAllPersonalityTraits();
      // Handle both response.data.data and response.data structures
      const allTraitsData = response.data?.data || response.data || [];
      setAllTraits(Array.isArray(allTraitsData) ? allTraitsData : []);
    } catch (error) {
      console.error('Failed to fetch all traits:', error);
      setAllTraits([]);
    }
  };

  const handleToggleTrait = (traitId) => {
    setSelectedTraitIds(prev => {
      if (prev.includes(traitId)) {
        return prev.filter(id => id !== traitId);
      } else {
        return [...prev, traitId];
      }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const currentTraitIds = userTraits.map(ut => ut.personalityTraitId);
      const toAdd = selectedTraitIds.filter(id => !currentTraitIds.includes(id));
      const toRemove = currentTraitIds.filter(id => !selectedTraitIds.includes(id));

      // Check if there are any changes
      if (toAdd.length === 0 && toRemove.length === 0) {
        toast.success('No changes to save');
        setShowSelector(false);
        setLoading(false);
        return;
      }

      // Remove traits first
      for (const traitId of toRemove) {
        const userTrait = userTraits.find(ut => ut.personalityTraitId === traitId);
        if (userTrait) {
          await profileExtendedService.deletePersonalityTrait(userTrait.id);
        }
      }

      // Then add new traits
      for (const traitId of toAdd) {
        try {
          await profileExtendedService.addPersonalityTrait(parseInt(traitId));
        } catch (error) {
          // Skip if already added
          if (!error.response?.data?.message?.includes('already added')) {
            throw error;
          }
        }
      }

      toast.success('Personality traits updated successfully');
      await fetchUserTraits();
      setShowSelector(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Failed to update personality traits');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (traitId) => {
    if (!window.confirm('Are you sure you want to remove this personality trait?')) return;

    try {
      await profileExtendedService.deletePersonalityTrait(traitId);
      toast.success('Personality trait removed successfully');
      fetchUserTraits();
    } catch (error) {
      toast.error('Failed to remove personality trait');
    }
  };

  const getAvailableTraits = () => {
    const userTraitIds = userTraits.map(ut => ut.personalityTraitId);
    return allTraits.filter(trait => !userTraitIds.includes(trait.id));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <div className="flex justify-between items-center mb-2.5">
        <h3 className="text-sm font-semibold text-primary-800 flex items-center gap-2">
          <Heart className="w-4 h-4 text-primary-600" />
          Personality Traits
        </h3>
        {!showSelector && (
          <Button
            onClick={() => {
              setSelectedTraitIds(userTraits.map(ut => ut.personalityTraitId));
              setShowSelector(true);
            }}
            variant="primary"
            size="sm"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add
          </Button>
        )}
      </div>

      {showSelector && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-semibold text-gray-800">Add Personality Trait</h4>
            <Button
              type="button"
              onClick={() => {
                setShowSelector(false);
                setSelectedTraitIds(userTraits.map(ut => ut.personalityTraitId));
              }}
              variant="ghost"
              className="p-0.5"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto mb-3">
            {allTraits.map((trait) => {
              const isSelected = selectedTraitIds.includes(trait.id);
              return (
                <label
                  key={trait.id}
                  className={`flex items-center gap-1.5 px-2 py-1.5 border rounded-md cursor-pointer transition-all text-xs ${
                    isSelected
                      ? 'bg-purple-50 border-purple-400 text-purple-700'
                      : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleTrait(trait.id)}
                    disabled={loading}
                    className="w-3 h-3 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
                  />
                  <span className="font-medium leading-tight break-words">{trait.name}</span>
                </label>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              type="button"
              onClick={() => {
                setShowSelector(false);
                setSelectedTraitIds(userTraits.map(ut => ut.personalityTraitId));
              }}
              variant="outline"
              disabled={loading}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading}
              variant="primary"
              loading={loading}
              size="sm"
            >
              Save
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
        {userTraits.length === 0 ? (
          <p className="text-gray-400 text-center py-6 text-xs w-full">No personality traits added yet. Click "Add" to get started.</p>
        ) : (
          userTraits.map((userTrait) => {
            const traitName = userTrait.trait?.name || userTrait.name || `Trait #${userTrait.personalityTraitId}`;
            return (
              <div
                key={userTrait.id}
                className="group relative pl-2.5 pr-6 py-1.5 bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100 transition-all border border-purple-200"
              >
                <span className="text-xs font-semibold leading-tight break-words">{traitName}</span>
                <Button
                  onClick={() => handleDelete(userTrait.id)}
                  variant="ghost"
                  className="absolute top-1/2 -translate-y-1/2 right-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-0.5 hover:bg-purple-200 rounded transition-all"
                >
                  <X className="w-2.5 h-2.5" />
                </Button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PersonalityTraitsSection;